/**
 * The nav's icons — one per route.
 *
 * Written by hand rather than pulled from an icon package: nine glyphs do not
 * justify a dependency, and an inline SVG inherits `currentColor` so the
 * active, hover and collapsed states need no second set of assets.
 *
 * They are **decoration, not information**. Every nav item keeps its text —
 * visible when the rail is open, `sr-only` when it is collapsed — so the
 * accessible name is the label and never a guess at what a shape means.
 */

const PATHS: Record<string, React.ReactNode> = {
  '/map': (
    <>
      <path d="M3 5.5 8 3.5l5 2 5-2v13l-5 2-5-2-5 2v-13Z" />
      <path d="M8 3.5v13M13 5.5v13" />
    </>
  ),
  '/dashboard': (
    <>
      <path d="M3.5 3.5h6v6h-6v-6ZM12.5 3.5h5v3.5h-5v-3.5Z" />
      <path d="M3.5 12.5h6v5h-6v-5ZM12.5 10h5v7.5h-5V10Z" />
    </>
  ),
  '/zones': (
    <>
      <path d="M10 3v4M5 11v-2.5h10V11" />
      <path d="M3 11h4v4H3v-4ZM13 11h4v4h-4v-4ZM8 3h4v4H8V3Z" />
    </>
  ),
  '/alerts': (
    <>
      <path d="M10 3a5 5 0 0 0-5 5c0 4-1.5 5.5-1.5 5.5h13S15 12 15 8a5 5 0 0 0-5-5Z" />
      <path d="M8.5 16a1.75 1.75 0 0 0 3 0" />
    </>
  ),
  '/reports': (
    <>
      <path d="M5 2.5h6l4 4v11H5v-15Z" />
      <path d="M11 2.5v4h4M7.5 11.5h5M7.5 14h3" />
    </>
  ),
  '/health': (
    <>
      <path d="M2.5 10.5h3l2-4.5 2.5 9 2-6 1.5 1.5h4" />
    </>
  ),
  '/admin/data': (
    <>
      <path d="M10 3c3.6 0 6.5 1 6.5 2.2S13.6 7.5 10 7.5 3.5 6.4 3.5 5.2 6.4 3 10 3Z" />
      <path d="M3.5 5.2v9.6c0 1.2 2.9 2.2 6.5 2.2s6.5-1 6.5-2.2V5.2" />
      <path d="M3.5 10c0 1.2 2.9 2.2 6.5 2.2s6.5-1 6.5-2.2" />
    </>
  ),
  '/admin/access': (
    <>
      <path d="M12.5 3.5a4 4 0 1 0-3.3 6.3L4 15v2.5h2.5l.8-.8v-1.7h1.7l.8-.8v-1.7h1.7l1-1a4 4 0 0 0 0-7.9Z" />
      <path d="M13.2 6.6h.01" />
    </>
  ),
  '/admin/alerting': (
    <>
      <path d="M3.5 6h5M11.5 6h5M3.5 14h3M9.5 14h7" />
      <path d="M9.5 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0ZM6.5 14a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Z" />
    </>
  ),
};

export function NavIcon({ to }: { to: string }) {
  const paths = PATHS[to];
  if (!paths) return null;
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 20 20"
      className="h-5 w-5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths}
    </svg>
  );
}
